import { createEntityAdapter, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IWorkloadGridRow } from '../types';
import { calculateWorkloadDistribution } from '../core/utilities/workload/distribution/calculateWorkloadDistribution';
import type { IState } from './store';

const adapter = createEntityAdapter<IWorkloadGridRow>();

export const workloadGridRowSelectors = adapter.getSelectors();

const slice = createSlice({
  name: 'workload',
  initialState: adapter.getInitialState(),
  reducers: {
    addManyWorkloadGridRows: (state, action: PayloadAction<IWorkloadGridRow[]>) => {
      return adapter.addMany(
        state,
        action.payload.map((row) => ({
          ...row,
          ...calculateWorkloadDistribution(row),
        }))
      );
    },
    removeAllWorkloadGridRows: (state) => {
      return adapter.removeAll(state);
    },
    updateOneWorkloadGridRow: adapter.updateOne,
  },
});

export const { addManyWorkloadGridRows, removeAllWorkloadGridRows, updateOneWorkloadGridRow } = slice.actions;

export const selectWorkloadGroupDictionary = createSelector(
  [(state: IState) => workloadGridRowSelectors.selectAll(state.workload)],
  (rows) => {
    return rows.reduce(
      (acc, row) => {
        acc[row.groupId] = row.groupName;
        acc[row.projectId] = `[${row.projectCode}] ${row.projectName}`;
        return acc;
      },
      {} as { [key: string]: string }
    );
  }
);

/**
 * Determines whether workload hours have been updated from the original value.
 */
export const selectIsWorkloadHoursDirty = createSelector(
  [(state: IState) => workloadGridRowSelectors.selectAll(state.workload)],
  (rows) => rows.some((row) => row.hours !== row.draftHours)
);

export default slice.reducer;
